import { useState, useEffect, useCallback } from 'react';
import { settingsAPI, aiAPI, dsgvoAPI, leadsAPI, processorsAPI } from '@/lib/api';
import { rbacAPI, authAPI, beratungAdminAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useSettings } from '@/contexts/SettingsContext';
import {
  Save, Plus, X, GripVertical, Brain, Globe, Copy, Check,
  Building2, Users, Sliders, Layers, FileText, Sparkles, Shield,
  ChevronDown, ChevronRight, ChevronUp, Trash2, Edit2, Lock, UserCog, ArrowUp, ArrowDown,
  Download, Search, AlertTriangle, Clock, Eye, UserCheck, Inbox, ThumbsUp, ThumbsDown, Plug, Smartphone, Mail, Award } from 'lucide-react';
import IntegrationsPage from '@/pages/IntegrationsPage';
import AppApiPage from '@/pages/AppApiPage';
import EmailKanalTab from '@/components/EmailKanalTab';
import WhatsAppAufraeumenKarte from '@/components/WhatsAppAufraeumenKarte';
import TeilnehmerlisteKarte from '@/components/TeilnehmerlisteKarte';
import GoogleBewertungKarte from '@/components/GoogleBewertungKarte';
import BewertungAutomatikKarte from '@/components/BewertungAutomatikKarte';
import DatenzugangKarte from '@/components/DatenzugangKarte';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import GraduierungRegelnTab from '@/components/GraduierungRegelnTab';

const FIELD_TYPES = [
  { value: 'text', label: 'Text' },
  { value: 'number', label: 'Nummer' },
  { value: 'select', label: 'Dropdown' },
  { value: 'date', label: 'Datum' },
  { value: 'checkbox', label: 'Checkbox' },
  { value: 'textarea', label: 'Langer Text' },
  { value: 'url', label: 'URL' },
  { value: 'email', label: 'E-Mail' },
  { value: 'phone', label: 'Telefon' },
];

/* Navigation der Einstellungen: vier Gruppen statt elf gleichrangiger Reiter.
   Jeder Bereich bringt seine eigene Erklärung mit, die über dem Inhalt steht —
   damit man ohne Vorwissen erkennt, was hier eingestellt wird und wo es wirkt. */
const SETTINGS_NAV = [
  {
    gruppe: 'Studio',
    items: [
      {
        key: 'general', label: 'Allgemein', icon: Sliders,
        titel: 'Allgemeine Einstellungen',
        beschreibung: 'Die Grunddaten deines Betriebs: Firmenname, Währung, Datumsformat und Steuersatz. '
          + 'Hier legst du außerdem fest, welche Mitgliedsarten und welche Status es gibt — diese Listen '
          + 'tauchen überall im CRM wieder auf, von der Mitgliederverwaltung bis zum Verkaufsrechner.',
      },
      {
        key: 'graduierung', label: 'Graduierung', icon: Award,
        titel: 'Gürtel und Graduierungsregeln',
        beschreibung: 'Welche Gürtelstufen es gibt und was ein Mitglied für die nächste Stufe erfüllen muss: '
          + 'Trainings gesamt, Haltezeit seit der letzten Graduierung und vorher vergebene Streifen. '
          + 'Sind alle drei erfüllt, meldet das CRM das Mitglied als reif — graduiert wird immer durch einen Trainer.',
      },
      {
        key: 'beratung', label: 'Beratungstermine', icon: Clock,
        titel: 'Öffentliche Beratungsbuchung',
        beschreibung: 'Steuert die Buchungsseite, über die Interessenten selbst einen Termin für das '
          + 'Erstgespräch wählen. Du legst fest, wer die Gespräche führt, wie lange sie dauern, wie viel '
          + 'Vorlauf nötig ist und an welchen Tagen Termine angeboten werden. Der QR-Code unten führt '
          + 'direkt auf die Buchungsseite — ideal für Flyer und Aushang.',
      },
    ],
  },
  {
    gruppe: 'Vertrieb',
    items: [
      {
        key: 'pipeline', label: 'Pipeline', icon: Layers,
        titel: 'Pipeline-Stufen',
        beschreibung: 'Die Stufen, die ein Interessent bei euch durchläuft — von der ersten Anfrage bis '
          + 'zum Abschluss. Reihenfolge und Farben bestimmen, wie die Pipeline-Ansicht und die Berichte '
          + 'aussehen. Ändere hier nur, was ihr wirklich anders macht: Jede Stufe muss im Alltag von '
          + 'jemandem gepflegt werden.',
      },
      {
        key: 'capture', label: 'Lead-Erfassung', icon: Globe,
        titel: 'Öffentliches Anfrageformular',
        beschreibung: 'Das Formular, über das Interessenten von eurer Website aus anfragen können. Ist es '
          + 'aktiv, landen Anfragen automatisch als neuer Lead im CRM — mit der Quelle „web_form" und '
          + 'ohne Abtippen. Den Link kannst du kopieren, verlinken oder als Seite einbetten.',
      },
      {
        key: 'sources', label: 'Lead-Quellen', icon: Users,
        titel: 'Woher die Leads kommen',
        beschreibung: 'Die Auswahlliste für die Herkunft eines Leads: Empfehlung, Instagram, Laufkundschaft, '
          + 'Google und so weiter. Je sauberer ihr das pflegt, desto klarer zeigen die Berichte später, '
          + 'welcher Kanal wirklich Mitglieder bringt — und wo Werbebudget sich lohnt.',
      },
      {
        key: 'custom-fields', label: 'Eigene Felder', icon: FileText,
        titel: 'Zusätzliche Felder',
        beschreibung: 'Wenn euch bei Leads oder Mitgliedschaften ein Feld fehlt, legt ihr es hier an — etwa '
          + 'Sportart, Schulklasse oder Zutrittskartennummer. Die Felder erscheinen danach automatisch in '
          + 'den Formularen. Weniger ist mehr: Jedes Feld will ausgefüllt werden.',
      },
      {
        key: 'ai', label: 'KI-Funktionen', icon: Brain,
        titel: 'KI im CRM',
        beschreibung: 'Schaltet die KI-Unterstützung frei: Bewertung der Erfolgsaussicht eines Leads und '
          + 'Textvorschläge für Nachrichten. Die Vorschläge sind immer nur Vorschläge — gesendet wird '
          + 'erst, wenn ein Mensch zustimmt. Der WhatsApp-Agent wird getrennt davon unter „KI-Agent" '
          + 'gesteuert.',
      },
    ],
  },
  {
    gruppe: 'Team & Recht',
    items: [
      {
        key: 'roles', label: 'Rollen & Rechte', icon: Shield,
        titel: 'Wer darf was',
        beschreibung: 'Hier legst du fest, welche Rolle welche Bereiche sehen und bearbeiten darf. Praktisch: '
          + 'Gib jedem nur das, was er für seine Arbeit braucht — Trainer brauchen Kursplan und Check-in, '
          + 'aber selten Umsatzberichte. Die Menüleiste passt sich automatisch an die Rechte an.',
      },
      {
        key: 'datenschutz', label: 'Datenschutz', icon: Lock,
        titel: 'Datenschutz & Einwilligungen',
        beschreibung: 'Alles, was die DSGVO von euch verlangt: Auskunft und Löschung zu einer Person, das '
          + 'Protokoll aller Zugriffe, offene Löschanfragen und die Liste eurer Dienstleister mit '
          + 'AVV-Status. Weiter unten stehen die Texte, die Mitglieder in der App unterschreiben.',
      },
    ],
  },
  {
    gruppe: 'Technik',
    items: [
      {
        key: 'integrations', label: 'Integrationen', icon: Plug,
        titel: 'Angebundene Dienste',
        beschreibung: 'Verbindungen zu anderen Systemen — Zahlungsdienstleister, E-Mail-Versand, Kalender '
          + 'und Messenger. Zugangsdaten werden verschlüsselt gespeichert und sind nach dem Speichern '
          + 'nicht mehr im Klartext lesbar.',
      },
      {
        key: 'email-kanal', label: 'E-Mail-Kanal', icon: Mail,
        titel: 'E-Mail als Zweitkanal',
        beschreibung: 'Terminbestätigungen gehen zusätzlich per E-Mail raus, Werbung nur mit '
          + 'bestätigter Einwilligung (Double-Opt-in). Hier siehst du den Zustand deiner '
          + 'Absenderdomain, pflegst Vorlagen und prüfst die letzten Sendungen. Den Versandweg '
          + 'selbst (SMTP/SendGrid) richtest du unter Integrationen ein.',
      },
      {
        key: 'app-api', label: 'Kunden-App-API', icon: Smartphone,
        titel: 'Schnittstelle zur Mitglieder-App',
        beschreibung: 'Die Schnittstelle, über die eure Mitglieder-App auf das CRM zugreift: API-Schlüssel '
          + 'erzeugen, sperren und den Zugriff nachvollziehen. Ein Schlüssel wird nur einmal angezeigt — '
          + 'notiere ihn beim Anlegen sicher.',
      },
    ],
  },
];

/* Bereiche, die über den globalen Speichern-Knopf oben gesichert werden.
   Die übrigen (Rollen, Datenschutz, Integrationen, App-API) speichern
   jeweils selbst — dort wird der Knopf ausgeblendet, damit er nicht
   fälschlich Sicherheit suggeriert. */
const GLOBAL_SAVE_TABS = ['general', 'pipeline', 'custom-fields', 'ai', 'capture', 'sources', 'beratung'];

const NAV_FLACH = SETTINGS_NAV.flatMap((g) => g.items);

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [tab, setTab] = useState('general');
  const [integrationsKanal, setIntegrationsKanal] = useState('sms');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [models, setModels] = useState([]);
  const [copied, setCopied] = useState(false);
  const [showAddStatusDialog, setShowAddStatusDialog] = useState(false);
  const { user } = useAuth();
  const { refresh: refreshGlobalSettings } = useSettings();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    const fetch = async () => {
      try {
        const [sRes, mRes] = await Promise.all([
          settingsAPI.get(),
          aiAPI.models().catch(() => ({ data: { models: [] } })),
        ]);
        setSettings(sRes.data);
        setModels(mRes.data.models || []);
      } catch (e) { console.error('Einstellungen laden fehlgeschlagen:', e); } finally { setLoading(false); }
    };
    fetch();
  }, []);

  const validateSettings = () => {
    const errors = [];

    if (!settings.company_name?.trim()) errors.push('Firmenname ist erforderlich');

    const blankStages = (settings.pipeline_stages || []).filter(s => !s.label?.trim() || !s.key?.trim());
    if (blankStages.length > 0) errors.push('Pipeline-Stufen dürfen keine leeren Namen oder Keys haben');
    if ((settings.pipeline_stages || []).length === 0) errors.push('Mindestens eine Pipeline-Stufe ist erforderlich');

    const blankSources = (settings.lead_sources || []).filter(s => !s?.trim());
    if (blankSources.length > 0) errors.push('Lead-Quellen dürfen nicht leer sein');

    const blankTypes = (settings.property_types || []).filter(t => !t?.trim());
    if (blankTypes.length > 0) errors.push('Mitgliedsarten dürfen nicht leer sein');

    const blankStatuses = (settings.property_statuses || []).filter(s => {
      const label = typeof s === 'object' ? s.label : s;
      return !label?.trim();
    });
    if (blankStatuses.length > 0) errors.push('Mitgliedsstatus darf nicht leer sein');

    const blankLeadFields = (settings.custom_fields_leads || []).filter(f => !f.label?.trim());
    if (blankLeadFields.length > 0) errors.push('Lead-Felder dürfen keine leeren Labels haben');

    const blankPropFields = (settings.custom_fields_properties || []).filter(f => !f.label?.trim());
    if (blankPropFields.length > 0) errors.push('Mitgliedschaft-Felder dürfen keine leeren Labels haben');

    const emptyDropdowns = [
      ...(settings.custom_fields_leads || []).filter(f => f.type === 'select' && (!f.options || f.options.length === 0 || f.options.every(o => !o?.trim()))),
      ...(settings.custom_fields_properties || []).filter(f => f.type === 'select' && (!f.options || f.options.length === 0 || f.options.every(o => !o?.trim()))),
    ];
    if (emptyDropdowns.length > 0) errors.push('Dropdown-Felder müssen mindestens eine Option haben');

    return errors;
  };

  const handleSave = async () => {
    if (!isAdmin) { toast.error('Nur für Admins'); return; }

    const errors = validateSettings();
    if (errors.length > 0) {
      toast.error(errors[0]);
      return;
    }

    // Clean data before saving — trim and remove fully blank entries
    const cleaned = {
      ...settings,
      company_name: settings.company_name?.trim(),
      lead_sources: (settings.lead_sources || []).map(s => s?.trim()).filter(Boolean),
      property_types: (settings.property_types || []).map(t => t?.trim()).filter(Boolean),
      property_statuses: (settings.property_statuses || []).filter(s => {
        const label = typeof s === 'object' ? s.label : s;
        return label?.trim();
      }),
      pipeline_stages: (settings.pipeline_stages || []).map(s => ({ ...s, label: s.label?.trim(), key: s.key?.trim() })),
      custom_fields_leads: (settings.custom_fields_leads || []).map(f => ({ ...f, label: f.label?.trim(), key: f.key?.trim() })),
      custom_fields_properties: (settings.custom_fields_properties || []).map(f => ({ ...f, label: f.label?.trim(), key: f.key?.trim() })),
    };

    setSaving(true);
    try {
      const res = await settingsAPI.update(cleaned);
      setSettings(res.data);
      refreshGlobalSettings();
      toast.success('Einstellungen gespeichert');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehlgeschlagen');
    } finally { setSaving(false); }
  };

  if (loading || !settings) return <div className="h-64 flex items-center justify-center"><div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6 fade-in max-w-6xl" data-testid="settings-page">
      <div className="flex items-start sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Einstellungen</h1>
          <p className="text-muted-foreground text-xs sm:text-sm">
            Zentrale Steuerung des CRM — links den Bereich wählen, rechts anpassen.
          </p>
        </div>
        {isAdmin && GLOBAL_SAVE_TABS.includes(tab) && (
          <Button onClick={handleSave} disabled={saving} data-testid="save-settings-btn" className="gap-2 flex-shrink-0">
            <Save className="w-4 h-4" /> <span className="hidden sm:inline">{saving ? 'Wird gespeichert...' : 'Speichern'}</span><span className="sm:hidden">{saving ? '...' : 'Speichern'}</span>
          </Button>
        )}
      </div>

      {!isAdmin && (
        <Card className="border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
          <CardContent className="p-4 text-sm text-amber-700 dark:text-amber-400">
            Nur Administratoren können Einstellungen ändern. Kontaktiere deinen Admin für Änderungen.
          </CardContent>
        </Card>
      )}

      <Tabs value={tab} onValueChange={setTab} className="w-full md:grid md:grid-cols-[210px_minmax(0,1fr)] md:gap-7 md:items-start">
        {/* Mobil: Auswahlliste mit Gruppen — spart die Reiter-Wurst */}
        <div className="md:hidden">
          <label className="text-xs text-muted-foreground" htmlFor="settings-bereich">Bereich</label>
          <select id="settings-bereich" value={tab} onChange={(e) => setTab(e.target.value)}
                  data-testid="settings-bereich-select"
                  className="mt-1 flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
            {SETTINGS_NAV.map((g) => (
              <optgroup key={g.gruppe} label={g.gruppe}>
                {g.items.map((i) => <option key={i.key} value={i.key}>{i.label}</option>)}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Desktop: gruppierte Seitennavigation */}
        <nav className="hidden md:block sticky top-4 space-y-4" aria-label="Einstellungsbereiche">
          {SETTINGS_NAV.map((g) => (
            <div key={g.gruppe}>
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground/70">
                {g.gruppe}
              </p>
              <div className="space-y-0.5">
                {g.items.map((i) => {
                  const Icon = i.icon;
                  const aktiv = tab === i.key;
                  return (
                    <button key={i.key} onClick={() => setTab(i.key)}
                            data-testid={`settings-tab-${i.key}`}
                            aria-current={aktiv ? 'page' : undefined}
                            className={`flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                              aktiv ? 'bg-primary/10 text-primary'
                                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'}`}>
                      <Icon className="w-4 h-4 flex-shrink-0" />
                      <span className="text-left">{i.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Rechte Spalte: Erklärung des Bereichs + Inhalt */}
        <div className="min-w-0">
          {(() => {
            const b = NAV_FLACH.find((i) => i.key === tab);
            if (!b) return null;
            return (
              <div className="mb-4 pb-4 border-b border-border/60">
                <h2 className="text-base font-semibold">{b.titel}</h2>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                  {b.beschreibung}
                </p>
              </div>
            );
          })()}

        {/* ─── General ─── */}
        <TabsContent value="general" className="mt-0">
          <Card className="border-border/60">
            <CardContent className="space-y-4 pt-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Firmenname</Label>
                  <Input value={settings.company_name || ''} onChange={(e) => setSettings(p => ({...p, company_name: e.target.value}))} disabled={!isAdmin} data-testid="company-name-input" />
                </div>
                <div>
                  <Label className="text-xs">Währung</Label>
                  <select value={settings.currency || 'EUR'} onChange={(e) => setSettings(p => ({...p, currency: e.target.value}))} disabled={!isAdmin} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    {['EUR', 'USD', 'GBP', 'CHF'].map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Datumsformat</Label>
                  <select value={settings.date_format || 'DD.MM.YYYY'} onChange={(e) => setSettings(p => ({...p, date_format: e.target.value}))} disabled={!isAdmin} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    {['DD.MM.YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">MwSt-Satz (%)</Label>
                  <Input type="number" step="0.1" min="0" max="100" value={settings.mwst_satz ?? 19} onChange={(e) => setSettings(p => ({...p, mwst_satz: parseFloat(e.target.value) || 0}))} disabled={!isAdmin} data-testid="mwst-input" />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Standard: 19 % (DE)</p>
                </div>
                <div className="space-y-1.5 md:col-span-2">
                  <Label className="text-xs">Google-Bewertungslink</Label>
                  <Input value={settings.bewertung_link || ''} onChange={(e) => setSettings(p => ({...p, bewertung_link: e.target.value}))} disabled={!isAdmin} placeholder="https://g.page/r/…/review" data-testid="bewertung-link-input" />
                  <p className="text-[11px] text-muted-foreground">Kommt in den Textvorschlag der Aufgabe „Bewertung erbitten“ (Onboarding, Tag 21).</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Mitgliedsarten</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(settings.property_types || []).map((t, i) => (
                      <div key={t} className="flex items-center gap-1 bg-muted rounded px-2 py-0.5">
                        <span className="text-xs capitalize">{t}</span>
                        {isAdmin && <button onClick={() => setSettings(p => ({...p, property_types: p.property_types.filter((_, j) => j !== i)}))}>
                          <X className="w-2.5 h-2.5 text-muted-foreground" /></button>}
                      </div>
                    ))}
                    {isAdmin && <button onClick={() => {
                      const name = prompt('Neue Mitgliedsart:');
                      if (name) setSettings(p => ({...p, property_types: [...(p.property_types || []), name.toLowerCase()]}));
                    }} className="text-xs text-primary hover:underline">+ Hinzufügen</button>}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Mitgliedsstatus</Label>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {(settings.property_statuses || []).map((s, i) => {
                      const label = typeof s === 'object' ? s.label : s;
                      const color = typeof s === 'object' ? s.color : null;
                      return (
                        <div key={typeof s === 'object' ? s.key : s} className="flex items-center gap-1 bg-muted rounded px-2 py-0.5">
                          {color && <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} />}
                          <span className="text-xs capitalize">{label}</span>
                          {isAdmin && <button onClick={() => setSettings(p => ({...p, property_statuses: p.property_statuses.filter((_, j) => j !== i)}))}>
                            <X className="w-2.5 h-2.5 text-muted-foreground" /></button>}
                        </div>
                      );
                    })}
                    {isAdmin && <button onClick={() => setShowAddStatusDialog(true)} className="text-xs text-primary hover:underline">+ Hinzufügen</button>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          <GoogleBewertungKarte />
          <BewertungAutomatikKarte />
        </TabsContent>

        {/* ─── Pipeline ─── */}
        <TabsContent value="pipeline" className="mt-0">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Pipeline-Stufen</CardTitle>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="add-stage-btn"
                    onClick={() => setSettings(p => ({...p, pipeline_stages: [...(p.pipeline_stages || []), {key: `stage_${Date.now()}`, label: 'Neue Stufe', color: '#6B7280'}]}))}>
                    <Plus className="w-3 h-3" />Stufe hinzufügen
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-0">
              <p className="text-xs text-muted-foreground mb-3">Stufen per Drag & Drop umsortieren. Sie erscheinen von links nach rechts auf dem Kanban-Board.</p>
              <PipelineStageList
                stages={settings.pipeline_stages || []}
                onChange={(stages) => setSettings(p => ({...p, pipeline_stages: stages}))}
                isAdmin={isAdmin}
              />
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Custom Fields ─── */}
        <TabsContent value="custom-fields" className="mt-0 space-y-4">
          <CustomFieldsSection
            title="Lead-Felder"
            description="Zusätzliche Felder für Leads anlegen, die bei der Erstellung und Bearbeitung angezeigt werden."
            icon={Users}
            fields={settings.custom_fields_leads || []}
            onChange={(fields) => setSettings(p => ({...p, custom_fields_leads: fields}))}
            isAdmin={isAdmin}
            testIdPrefix="lead"
          />
          <CustomFieldsSection
            title="Mitgliedschaft-Felder"
            description="Zusätzliche Felder für Mitgliedschaften und Verträge. Ideal für Trainingsplan, Zutrittskarten etc."
            icon={Building2}
            fields={settings.custom_fields_properties || []}
            onChange={(fields) => setSettings(p => ({...p, custom_fields_properties: fields}))}
            isAdmin={isAdmin}
            testIdPrefix="property"
          />
        </TabsContent>

        {/* ─── AI ─── */}
        <TabsContent value="ai" className="mt-0">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="w-4 h-4 text-primary" />
                  <CardTitle className="text-sm">KI-Funktionen</CardTitle>
                </div>
                <Badge variant={settings.ai_enabled ? 'default' : 'secondary'} className="text-[10px]">
                  {settings.ai_enabled ? 'Aktiviert' : 'Deaktiviert'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">KI-Funktionen aktivieren</p>
                  <p className="text-xs text-muted-foreground">Lead-Scoring, automatische Texterstellung</p>
                </div>
                <Switch checked={settings.ai_enabled || false} onCheckedChange={(v) => setSettings(p => ({...p, ai_enabled: v}))} disabled={!isAdmin} data-testid="ai-enabled-toggle" />
              </div>
              {settings.ai_enabled && (
                <div>
                  <Label className="text-xs">KI-Modell</Label>
                  <select value={settings.ai_model || 'gpt-5.2'} onChange={(e) => setSettings(p => ({...p, ai_model: e.target.value}))} disabled={!isAdmin} data-testid="ai-model-select" className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    {models.map(m => <option key={m.key} value={m.key}>{m.label}</option>)}
                  </select>
                  <p className="text-[10px] text-muted-foreground mt-1">Credits werden vom Universal Key Guthaben abgezogen.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Lead Capture ─── */}
        <TabsContent value="capture" className="mt-0">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <CardTitle className="text-sm">Lead-Erfassungsformular</CardTitle>
                </div>
                <Badge variant={settings.capture_form_enabled !== false ? 'default' : 'secondary'} className="text-[10px]">
                  {settings.capture_form_enabled !== false ? 'Aktiv' : 'Deaktiviert'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">Öffentliches Formular aktivieren</p>
                  <p className="text-xs text-muted-foreground">Lead-Anfragen über die Website annehmen</p>
                </div>
                <Switch checked={settings.capture_form_enabled !== false} onCheckedChange={(v) => setSettings(p => ({...p, capture_form_enabled: v}))} disabled={!isAdmin} data-testid="capture-form-toggle" />
              </div>
              {settings.capture_form_enabled !== false && (
                <div className="bg-muted/50 rounded-lg p-4 border space-y-3">
                  <p className="text-xs font-medium">Diesen Link teilen oder auf der Website einbetten:</p>
                  <div className="flex items-center gap-2">
                    <Input readOnly value={`${window.location.origin}/capture`} className="h-9 text-xs mono flex-1" data-testid="capture-form-url" />
                    <Button size="sm" variant="outline" className="h-9 gap-1.5" data-testid="copy-capture-url"
                      onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/capture`); setCopied(true); setTimeout(() => setCopied(false), 2000); }}>
                      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Kopiert' : 'Kopieren'}
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground">Leads erscheinen als &quot;Neu&quot; mit Quelle &quot;web_form&quot;.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Sources ─── */}
        <TabsContent value="sources" className="mt-0">
          <Card className="border-border/60">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm">Lead-Quellen</CardTitle>
                {isAdmin && (
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1" data-testid="add-source-btn"
                    onClick={() => setSettings(p => ({...p, lead_sources: [...(p.lead_sources || []), '']}))}>
                    <Plus className="w-3 h-3" />Quelle hinzufügen
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-xs text-muted-foreground mb-3">Definiere, woher deine Leads kommen. Diese erscheinen als Filteroptionen und im Lead-Formular.</p>
              <div className="flex flex-wrap gap-2">
                {(settings.lead_sources || []).map((source, i) => (
                  <div key={`${source}-${i}`} className="flex items-center gap-1 bg-muted rounded-md px-2 py-1">
                    <Input value={source} onChange={(e) => {
                      const sources = [...settings.lead_sources];
                      sources[i] = e.target.value;
                      setSettings(p => ({...p, lead_sources: sources}));
                    }} className="h-7 w-28 text-xs border-0 bg-transparent p-0" disabled={!isAdmin} />
                    {isAdmin && (
                      <button onClick={() => setSettings(p => ({...p, lead_sources: p.lead_sources.filter((_, j) => j !== i)}))} className="text-muted-foreground hover:text-destructive">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Roles & Permissions ─── */}
        <TabsContent value="roles" className="mt-0">
          <RolesPermissionsTab />
        </TabsContent>

        {/* ─── Beratungs-Verfügbarkeit (öffentliche Erstberatungs-Buchung) ─── */}
        <TabsContent value="graduierung" className="mt-0">
          <GraduierungRegelnTab settings={settings} setSettings={setSettings} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="beratung" className="mt-0">
          <BeratungTab settings={settings} setSettings={setSettings} isAdmin={isAdmin} />
        </TabsContent>

        {/* ─── Datenschutz / DSGVO ─── */}
        <TabsContent value="datenschutz" className="mt-0">
          <DatenschutzTab />
        </TabsContent>

        {/* ─── Integrationen (eingebettet, nur über die Admin-Einstellungen erreichbar) ─── */}
        <TabsContent value="integrations" className="mt-0">
          <IntegrationsPage kanal={integrationsKanal} />
        </TabsContent>

        {/* ─── E-Mail-Kanal (Erweiterung v2, Modul A) ─── */}
        <TabsContent value="email-kanal" className="mt-0">
          <EmailKanalTab onZumVersandweg={() => { setIntegrationsKanal('email'); setTab('integrations'); }} />
        </TabsContent>

        {/* ─── Kunden-App-API (eingebettet, nur über die Admin-Einstellungen erreichbar) ─── */}
        <TabsContent value="app-api" className="mt-0">
          <AppApiPage />
        </TabsContent>
        </div>
      </Tabs>

      {/* Add Status Dialog */}
      <AddPropertyStatusDialog
        open={showAddStatusDialog}
        onClose={() => setShowAddStatusDialog(false)}
        onAdd={(statusName, statusColor) => {
          setSettings(p => ({
            ...p, 
            property_statuses: [
              ...(p.property_statuses || []), 
              { key: statusName.toLowerCase(), label: statusName, color: statusColor || '#6B7280' }
            ]
          }));
        }}
      />
    </div>
  );
}


/* ─── Custom Fields Section ─── */
function CustomFieldsSection({ title, description, icon: Icon, fields, onChange, isAdmin, testIdPrefix }) {
  const [expandedField, setExpandedField] = useState(null);
  const [sectionOpen, setSectionOpen] = useState(true);

  const addField = () => {
    const newField = {
      key: `field_${Date.now()}`,
      label: 'New Field',
      type: 'text',
      required: false,
      options: [],
      placeholder: '',
    };
    onChange([...fields, newField]);
    setExpandedField(fields.length);
    if (!sectionOpen) setSectionOpen(true);
  };

  const commitField = (index, updated) => {
    const newFields = fields.map((f, i) => i === index ? updated : f);
    onChange(newFields);
  };

  const removeField = (index) => {
    onChange(fields.filter((_, i) => i !== index));
    if (expandedField === index) setExpandedField(null);
  };

  const TYPE_LABELS = { text: 'Text', number: 'Nummer', select: 'Dropdown', date: 'Datum', checkbox: 'Checkbox', textarea: 'Langer Text', url: 'URL', email: 'E-Mail', phone: 'Telefon' };

  return (
    <Card className="border-border/60" data-testid={`${testIdPrefix}-custom-fields`}>
      <button type="button" onClick={() => setSectionOpen(!sectionOpen)} className="w-full flex items-center justify-between px-6 py-4 hover:bg-muted/10 transition-colors">
        <div className="flex items-center gap-2">
          {sectionOpen ? <ChevronDown className="w-4 h-4 text-muted-foreground" /> : <ChevronRight className="w-4 h-4 text-muted-foreground" />}
          <Icon className="w-4 h-4 text-primary" />
          <div className="text-left">
            <p className="text-sm font-semibold">{title}</p>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-[10px]">{fields.length} Feld{fields.length !== 1 ? 'er' : ''}</Badge>
          {isAdmin && (
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={(e) => { e.stopPropagation(); addField(); }} data-testid={`add-${testIdPrefix}-field-btn`}>
              <Plus className="w-3 h-3" /> Hinzufügen
            </Button>
          )}
        </div>
      </button>
      {sectionOpen && (
        <CardContent className="pt-0 pb-4">
          {fields.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-20" />
              <p className="text-sm">Noch keine Felder definiert</p>
              {isAdmin && <p className="text-xs mt-1">Klicke auf &quot;Hinzufügen&quot; um ein Feld zu erstellen</p>}
            </div>
          ) : (
            <div className="space-y-1.5">
              {fields.map((field, i) => {
                const isExpanded = expandedField === i;
                return (
                  <div key={field.key || i} className={`border rounded-lg overflow-hidden transition-all ${isExpanded ? 'border-primary/30 bg-primary/[0.02]' : 'border-border/40'}`} data-testid={`${testIdPrefix}-field-${i}`}>
                    <button
                      type="button"
                      onClick={() => setExpandedField(isExpanded ? null : i)}
                      className="w-full flex items-center justify-between px-3 py-2 hover:bg-muted/20 transition-colors"
                    >
                      <div className="flex items-center gap-2.5">
                        <GripVertical className="w-3 h-3 text-muted-foreground/30" />
                        <span className="text-sm font-medium">{field.label || 'Untitled'}</span>
                        <Badge variant="secondary" className="text-[9px] h-4">{TYPE_LABELS[field.type] || field.type}</Badge>
                        {field.required && <Badge variant="default" className="text-[9px] h-4">Pflicht</Badge>}
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] mono text-muted-foreground hidden sm:inline">{field.key}</span>
                        {isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
                      </div>
                    </button>
                    {isExpanded && (
                      <FieldEditor
                        field={field}
                        index={i}
                        isAdmin={isAdmin}
                        typeLabels={TYPE_LABELS}
                        onCommit={(updated) => commitField(i, updated)}
                        onRemove={() => removeField(i)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}


/* ─── Field Editor (local state to prevent re-render on each keystroke) ─── */
function FieldEditor({ field, index, isAdmin, typeLabels, onCommit, onRemove }) {
  const [local, setLocal] = useState({ ...field });

  // Sync local state when field changes externally (e.g., parent saves)
  useEffect(() => {
    setLocal({ ...field });
  }, [field]);

  const update = (key, value) => {
    const updated = { ...local, [key]: value };
    // Auto-generate key from label
    if (key === 'label') {
      const generated = value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
      updated.key = generated || field.key;  // keep original key if label produces empty key
    }
    setLocal(updated);
    // Don't commit immediately - only update local state
  };

  // Commit on blur for text fields
  const commitOnBlur = () => onCommit(local);

  const addOption = () => {
    const updated = { ...local, options: [...(local.options || []), ''] };
    setLocal(updated);
    onCommit(updated);
  };

  const updateOption = (optIndex, value) => {
    const opts = [...(local.options || [])];
    opts[optIndex] = value;
    setLocal({ ...local, options: opts });
    // Don't commit immediately - wait for blur
  };

  const removeOption = (optIndex) => {
    const updated = { ...local, options: (local.options || []).filter((_, i) => i !== optIndex) };
    setLocal(updated);
    onCommit(updated);
  };

  return (
    <div className="px-3 pb-3 pt-1 border-t space-y-3">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div>
          <Label className="text-[10px] text-muted-foreground">Feldbezeichnung</Label>
          <Input
            value={local.label}
            onChange={(e) => update('label', e.target.value)}
            onBlur={commitOnBlur}
            className="h-8 text-xs"
            disabled={!isAdmin}
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Typ</Label>
          <select
            value={local.type}
            onChange={(e) => { update('type', e.target.value); onCommit({ ...local, type: e.target.value }); }}
            disabled={!isAdmin}
            className="flex h-8 w-full rounded-md border border-input bg-background px-2 text-xs shadow-sm"
          >
            {Object.entries(typeLabels).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="flex items-end gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={local.required || false}
              onCheckedChange={(v) => { update('required', v); onCommit({ ...local, required: v }); }}
              disabled={!isAdmin}
            />
            <Label className="text-[10px]">Pflichtfeld</Label>
          </div>
          {isAdmin && (
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 ml-auto text-muted-foreground hover:text-destructive" onClick={onRemove}>
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </div>
      <div>
        <Label className="text-[10px] text-muted-foreground">Platzhalter</Label>
        <Input
          value={local.placeholder || ''}
          onChange={(e) => update('placeholder', e.target.value)}
          onBlur={commitOnBlur}
          className="h-7 text-xs max-w-sm"
          placeholder="Hinweistext im leeren Feld"
          disabled={!isAdmin}
        />
      </div>
      {local.type === 'select' && (
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-[10px] text-muted-foreground">Dropdown-Optionen</Label>
            {isAdmin && <button type="button" onClick={addOption} className="text-[10px] text-primary hover:underline">+ Option hinzufügen</button>}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {(local.options || []).map((opt, j) => (
              <div key={`${opt}-${j}`} className="flex items-center gap-1 bg-muted rounded px-2 py-0.5">
                <Input
                  value={opt}
                  onChange={(e) => updateOption(j, e.target.value)}
                  onBlur={() => onCommit(local)}
                  className="h-6 w-24 text-[11px] border-0 bg-transparent p-0"
                  placeholder="Option..."
                  disabled={!isAdmin}
                />
                {isAdmin && <button type="button" onClick={() => removeOption(j)} className="text-muted-foreground hover:text-destructive"><X className="w-2.5 h-2.5" /></button>}
              </div>
            ))}
            {(local.options || []).length === 0 && <p className="text-[10px] text-muted-foreground">Noch keine Optionen</p>}
          </div>
        </div>
      )}
      <p className="text-[9px] text-muted-foreground mono">key: {local.key}</p>
    </div>
  );
}


/* ─── Roles & Permissions Tab ─── */
function RolesPermissionsTab() {
  const [roles, setRoles] = useState({ default_roles: [], custom_roles: [] });
  const [sections, setSections] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newRoleName, setNewRoleName] = useState('');
  const [newRoleDesc, setNewRoleDesc] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [rRes, pRes, uRes] = await Promise.all([
        rbacAPI.getRoles(),
        rbacAPI.getPermissions(),
        authAPI.users().catch(() => ({ data: [] })),
      ]);
      setRoles(rRes.data);
      setSections(pRes.data.sections || []);
      setUsers(uRes.data || []);
    } catch (e) {  } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allRoles = [...(roles.default_roles || []), ...(roles.custom_roles || [])];

  const [roleError, setRoleError] = useState('');

  const validateRoleName = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return 'Rollenname ist erforderlich';
    if (trimmed.length < 2) return 'Rollenname muss mind. 2 Zeichen haben';
    if (trimmed.length > 50) return 'Rollenname darf max. 50 Zeichen haben';
    if (!/[A-Za-zÄäÖöÜüß]/.test(trimmed)) return 'Rollenname muss mind. einen Buchstaben enthalten';
    if (!/^[A-Za-zÄäÖöÜüß0-9\s\-]+$/.test(trimmed)) return 'Nur Buchstaben, Zahlen, Leerzeichen und Bindestriche erlaubt';
    if (/[\-]{2,}/.test(trimmed)) return 'Keine aufeinanderfolgenden Bindestriche';
    return null;
  };

  const handleCreateRole = async () => {
    const err = validateRoleName(newRoleName);
    if (err) { setRoleError(err); return; }
    setSaving(true);
    setRoleError('');
    try {
      await rbacAPI.createRole({ name: newRoleName.trim(), description: newRoleDesc.trim() });
      toast.success('Rolle erstellt');
      setShowCreate(false); setNewRoleName(''); setNewRoleDesc(''); setRoleError('');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const handleSavePermissions = async () => {
    if (!editingRole || editingRole.is_default) return;
    setSaving(true);
    try {
      await rbacAPI.updateRole(editingRole.id, {
        name: editingRole.name,
        description: editingRole.description,
        permissions: editingRole.permissions,
      });
      toast.success('Berechtigungen gespeichert');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await rbacAPI.deleteRole(roleId);
      toast.success('Rolle gelöscht');
      if (selectedRole?.id === roleId) { setSelectedRole(null); setEditingRole(null); }
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const handleAssignRole = async (userId, roleId) => {
    try {
      await rbacAPI.assignRole(userId, roleId);
      toast.success('Rolle zugewiesen');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Fehlgeschlagen'); }
  };

  const togglePermission = (key) => {
    if (!editingRole || editingRole.is_default) return;
    setEditingRole(prev => ({
      ...prev,
      permissions: { ...prev.permissions, [key]: !prev.permissions[key] }
    }));
  };

  const toggleSection = (sectionPerms, allEnabled) => {
    if (!editingRole || editingRole.is_default) return;
    const updates = {};
    sectionPerms.forEach(p => { updates[p.key] = !allEnabled; });
    setEditingRole(prev => ({
      ...prev,
      permissions: { ...prev.permissions, ...updates }
    }));
  };

  if (loading) return <div className="h-32 flex items-center justify-center"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  const viewRole = editingRole || selectedRole;

  return (
    <div className="space-y-4" data-testid="roles-permissions-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold">Rollen & Berechtigungen</h3>
          <p className="text-xs text-muted-foreground">Zugriffskontrolle für dein Team verwalten</p>
        </div>
        <Button size="sm" onClick={() => setShowCreate(true)} className="gap-1.5" data-testid="create-role-btn">
          <Plus className="w-3.5 h-3.5" /> Neue Rolle
        </Button>
      </div>

      {/* Create Role Inline */}
      {showCreate && (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Rollenname *</Label>
                <Input value={newRoleName} onChange={(e) => { setNewRoleName(e.target.value); setRoleError(''); }} placeholder="z.B. Teamleiter, Trainer..." className={`h-8 text-xs ${roleError ? 'border-destructive' : ''}`} data-testid="new-role-name" />
                {roleError && <p className="text-[10px] text-destructive mt-0.5">{roleError}</p>}
              </div>
              <div><Label className="text-xs">Beschreibung</Label><Input value={newRoleDesc} onChange={(e) => setNewRoleDesc(e.target.value)} placeholder="Kurze Beschreibung..." className="h-8 text-xs" /></div>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={handleCreateRole} disabled={saving} className="h-7 text-xs" data-testid="save-new-role">{saving ? 'Wird erstellt...' : 'Erstellen'}</Button>
              <Button size="sm" variant="outline" onClick={() => { setShowCreate(false); setRoleError(''); }} className="h-7 text-xs">Abbrechen</Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Role List */}
        <div className="space-y-2">
          <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Rollen</p>
          {allRoles.map((role) => {
            const isSelected = viewRole?.id === role.id;
            const userCount = users.filter(u => u.role === role.id).length;
            return (
              <div
                key={role.id}
                onClick={() => { setSelectedRole(role); setEditingRole(role.is_default ? null : { ...role }); }}
                data-testid={`role-item-${role.id}`}
                className={`p-3 rounded-lg border cursor-pointer transition-all ${
                  isSelected ? 'border-primary bg-primary/5' : 'border-border/60 hover:border-primary/30'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {role.is_default ? <Lock className="w-3.5 h-3.5 text-muted-foreground" /> : <Shield className="w-3.5 h-3.5 text-primary" />}
                    <span className="text-sm font-medium">{role.name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="secondary" className="text-[9px]">{userCount} Nutzer</Badge>
                    {!role.is_default && (
                      <button onClick={(e) => { e.stopPropagation(); handleDeleteRole(role.id); }} className="text-muted-foreground hover:text-destructive p-0.5">
                        <Trash2 className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-1 line-clamp-1">{role.description}</p>
                {role.is_default && <Badge variant="secondary" className="text-[8px] mt-1">Standard — Schreibgeschützt</Badge>}
              </div>
            );
          })}
        </div>

        {/* Permission Editor */}
        <div className="lg:col-span-2">
          {viewRole ? (
            <Card className="border-border/60">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-sm flex items-center gap-2">
                      {viewRole.is_default ? <Lock className="w-4 h-4 text-muted-foreground" /> : <Shield className="w-4 h-4 text-primary" />}
                      {viewRole.name} — Berechtigungen
                    </CardTitle>
                    <p className="text-xs text-muted-foreground mt-0.5">{viewRole.description}</p>
                  </div>
                  {editingRole && !editingRole.is_default && (
                    <Button size="sm" onClick={handleSavePermissions} disabled={saving} className="gap-1.5" data-testid="save-permissions-btn">
                      <Save className="w-3.5 h-3.5" /> {saving ? 'Speichern...' : 'Speichern'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-1 max-h-[500px] overflow-y-auto">
                {sections.map((section) => {
                  const perms = viewRole.is_default ? (selectedRole?.permissions || {}) : (editingRole?.permissions || {});
                  const sectionPerms = section.permissions;
                  const enabledCount = sectionPerms.filter(p => perms[p.key]).length;
                  const allEnabled = enabledCount === sectionPerms.length;
                  return (
                    <PermissionSection
                      key={section.key}
                      section={section}
                      permissions={perms}
                      isReadOnly={viewRole.is_default}
                      enabledCount={enabledCount}
                      allEnabled={allEnabled}
                      onToggle={togglePermission}
                      onToggleSection={() => toggleSection(sectionPerms, allEnabled)}
                    />
                  );
                })}
              </CardContent>
            </Card>
          ) : (
            <Card className="border-border/60">
              <CardContent className="p-12 text-center text-muted-foreground">
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Wähle eine Rolle aus, um Berechtigungen zu bearbeiten</p>
              </CardContent>
            </Card>
          )}

          {/* Users with this role */}
          {viewRole && (
            <Card className="border-border/60 mt-4">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2"><UserCog className="w-4 h-4" />Nutzer mit Rolle &quot;{viewRole.name}&quot;</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Warning for empty permissions */}
                {!viewRole.is_default && viewRole.permissions && Object.values(viewRole.permissions).filter(Boolean).length === 0 && (
                  <div className="mb-3 p-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 text-xs text-amber-700 dark:text-amber-400 flex items-start gap-2">
                    <Shield className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium">Keine Berechtigungen konfiguriert</p>
                      <p className="mt-0.5">Diese Rolle hat keine aktiven Berechtigungen. Zugewiesene Nutzer erhalten Standard-Agent-Zugriff. Konfiguriere Berechtigungen oben, bevor du Nutzer zuweist.</p>
                    </div>
                  </div>
                )}
                {/* Assign user to this role */}
                {!viewRole.is_default && (
                  <div className="mb-3 flex items-center gap-2">
                    <select
                      defaultValue=""
                      onChange={async (e) => {
                        if (!e.target.value) return;
                        const permsCount = editingRole?.permissions ? Object.values(editingRole.permissions).filter(Boolean).length : 0;
                        if (permsCount === 0) {
                          const proceed = window.confirm('Diese Rolle hat keine Berechtigungen. Der Nutzer erhält Standard-Agent-Zugriff. Fortfahren?');
                          if (!proceed) { e.target.value = ''; return; }
                        }
                        await handleAssignRole(e.target.value, viewRole.id);
                        e.target.value = '';
                      }}
                      className="flex-1 h-8 text-xs rounded-md border border-input bg-background px-2"
                      data-testid="assign-user-to-role"
                    >
                      <option value="">Nutzer dieser Rolle zuweisen...</option>
                      {users.filter(u => u.role !== viewRole.id).map(u => (
                        <option key={u.user_id} value={u.user_id}>{u.name} ({u.email}) — aktuell {allRoles.find(r => r.id === u.role)?.name || u.role}</option>
                      ))}
                    </select>
                  </div>
                )}
                {users.filter(u => u.role === viewRole.id).length === 0 ? (
                  <p className="text-xs text-muted-foreground text-center py-3">Noch keine Nutzer zugewiesen{!viewRole.is_default ? ' — nutze das Dropdown oben' : ''}</p>
                ) : (
                  <div className="space-y-2">
                    {users.filter(u => u.role === viewRole.id).map((u) => (
                      <div key={u.user_id} className="flex items-center justify-between py-1.5">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary">{u.name?.charAt(0)}</div>
                          <div><p className="text-xs font-medium">{u.name}</p><p className="text-[10px] text-muted-foreground">{u.email}</p></div>
                        </div>
                        <select
                          value={u.role}
                          onChange={(e) => handleAssignRole(u.user_id, e.target.value)}
                          className="h-7 text-[10px] rounded border border-input bg-background px-2"
                        >
                          {allRoles.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}


function PermissionSection({ section, permissions, isReadOnly, enabledCount, allEnabled, onToggle, onToggleSection }) {
  const [open, setOpen] = useState(false);
  const total = section.permissions.length;

  return (
    <div className="border rounded-lg overflow-hidden" data-testid={`perm-section-${section.key}`}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-muted/30 transition-colors"
      >
        <div className="flex items-center gap-2">
          {open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />}
          <span className="text-xs font-semibold">{section.label}</span>
          <Badge variant={enabledCount === total ? 'default' : enabledCount > 0 ? 'secondary' : 'outline'} className="text-[8px] h-4">
            {enabledCount}/{total}
          </Badge>
        </div>
        {!isReadOnly && (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSection(); }}
            className="text-[10px] text-primary hover:underline"
          >
            {allEnabled ? 'Alle deaktivieren' : 'Alle aktivieren'}
          </button>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 space-y-1.5 border-t bg-muted/10">
          {section.permissions.map((perm) => {
            const enabled = !!permissions[perm.key];
            return (
              <label
                key={perm.key}
                className={`flex items-center gap-3 py-1.5 px-2 rounded-md transition-colors ${
                  isReadOnly ? '' : 'cursor-pointer hover:bg-muted/30'
                }`}
              >
                <input
                  type="checkbox"
                  checked={enabled}
                  onChange={() => !isReadOnly && onToggle(perm.key)}
                  disabled={isReadOnly}
                  className="rounded border-input h-3.5 w-3.5 accent-primary"
                  data-testid={`perm-${perm.key}`}
                />
                <span className={`text-xs ${enabled ? 'text-foreground' : 'text-muted-foreground'}`}>{perm.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
}



/* ─── Drag-and-Drop Pipeline Stage List ─── */
function PipelineStageList({ stages, onChange, isAdmin }) {
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Make the drag image slightly transparent
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDragIndex(null);
    setOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === dropIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const updated = [...stages];
    const [moved] = updated.splice(dragIndex, 1);
    updated.splice(dropIndex, 0, moved);
    onChange(updated);
    setDragIndex(null);
    setOverIndex(null);
  };

  const updateStage = (index, field, value) => {
    const updated = [...stages];
    updated[index] = { ...updated[index], [field]: value };
    onChange(updated);
  };

  const removeStage = (index) => {
    onChange(stages.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-0">
      {stages.map((stage, i) => (
        <div
          key={stage.key || i}
          draggable={isAdmin}
          onDragStart={(e) => handleDragStart(e, i)}
          onDragEnd={handleDragEnd}
          onDragOver={(e) => handleDragOver(e, i)}
          onDrop={(e) => handleDrop(e, i)}
          className={`flex flex-wrap sm:flex-nowrap items-center gap-2 group py-2 px-1 rounded-md transition-all ${
            isAdmin ? 'cursor-grab active:cursor-grabbing' : ''
          } ${overIndex === i && dragIndex !== i ? 'bg-primary/10 border-primary/30 border-dashed border' : 'border border-transparent'
          } ${dragIndex === i ? 'opacity-50' : ''}`}
          data-testid={`stage-row-${i}`}
        >
          <GripVertical className={`w-4 h-4 flex-shrink-0 ${isAdmin ? 'text-muted-foreground/50 group-hover:text-muted-foreground' : 'text-muted-foreground/20'}`} />
          <span className="text-[10px] text-muted-foreground w-5 text-center flex-shrink-0">{i + 1}</span>
          <input
            type="color"
            value={stage.color}
            onChange={(e) => updateStage(i, 'color', e.target.value)}
            className="w-8 h-8 rounded border cursor-pointer flex-shrink-0"
            disabled={!isAdmin}
          />
          <Input
            value={stage.label}
            onChange={(e) => updateStage(i, 'label', e.target.value)}
            className="flex-1 h-9"
            disabled={!isAdmin}
            data-testid={`stage-label-${i}`}
          />
          <Input
            value={stage.key}
            onChange={(e) => updateStage(i, 'key', e.target.value)}
            className="w-full sm:w-32 h-9 mono text-xs"
            disabled={!isAdmin}
            placeholder="key"
          />
          {isAdmin && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0 flex-shrink-0 opacity-0 group-hover:opacity-100"
              onClick={() => removeStage(i)}
            >
              <X className="w-4 h-4 text-muted-foreground" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
}



/* ─── Add Status Dialog ─── */
function AddPropertyStatusDialog({ open, onClose, onAdd }) {
  const [statusName, setStatusName] = useState('');
  const [statusColor, setStatusColor] = useState('#6B7280');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (statusName.trim()) {
      onAdd(statusName.trim(), statusColor);
      setStatusName('');
      setStatusColor('#6B7280');
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Mitgliedsstatus hinzufügen</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label>Statusname *</Label>
            <Input
              value={statusName}
              onChange={(e) => setStatusName(e.target.value)}
              placeholder="z.B. Aktiv, Gekündigt, Pausiert"
              autoFocus
              required
            />
          </div>
          <div>
            <Label>Farbe</Label>
            <div className="flex items-center gap-3">
              <input
                type="color"
                value={statusColor}
                onChange={(e) => setStatusColor(e.target.value)}
                className="w-12 h-10 rounded border cursor-pointer"
              />
              <Input
                value={statusColor}
                onChange={(e) => setStatusColor(e.target.value)}
                placeholder="#6B7280"
                className="flex-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Abbrechen</Button>
            <Button type="submit">Hinzufügen</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}


// ─── Datenschutz / DSGVO Tab ───

// ─── Einwilligungstexte (App-Waiver, Feature 2) ───
function ConsentTextsCard() {
  const [texts, setTexts] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingType, setSavingType] = useState(null);
  // Freigabe einer Vorlage braucht einen zweiten, bewussten Klick (kein window.confirm):
  // der Typ, dessen Freigabe gerade bestätigt werden soll.
  const [freigabeBestaetigen, setFreigabeBestaetigen] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await dsgvoAPI.consentTexts();
      const list = res.data.texts || [];
      setTexts(list);
      setDrafts(Object.fromEntries(list.map(t => [t.type, t.body])));
    } catch { toast.error('Einwilligungstexte konnten nicht geladen werden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (t) => {
    const body = (drafts[t.type] ?? '').trim();
    if (!body) { toast.error('Text darf nicht leer sein'); return; }
    // Eine Vorlage darf auch mit unverändertem Wortlaut freigegeben werden: Der Server
    // legt bei jedem Speichern eine neue Version an und nimmt die Vorlagen-Markierung weg.
    // Die Sperre „Keine Änderung" gilt nur für bereits freigegebene Texte.
    const warVorlage = !!t.is_placeholder;
    if (body === t.body && !warVorlage) { toast.info('Keine Änderung'); return; }
    setFreigabeBestaetigen(null);
    setSavingType(t.type);
    try {
      const res = await dsgvoAPI.updateConsentText(t.type, body);
      if (warVorlage) {
        const version = res?.data?.version ?? ((t.version || 0) + 1);
        toast.success(`Freigegeben — Version ${version} gilt ab jetzt`);
      } else {
        toast.success(`${t.title} gespeichert (neue Version)`);
      }
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Speichern fehlgeschlagen'); }
    finally { setSavingType(null); }
  };

  const anyPlaceholder = texts.some(t => t.is_placeholder);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2"><FileText className="w-4 h-4" />Einwilligungstexte (App)</CardTitle>
        <p className="text-xs text-muted-foreground mt-1">
          Die Texte, die Mitglieder in der App unterschreiben. Jede Änderung erzeugt eine neue Version; bereits erteilte Einwilligungen bleiben der alten Version zugeordnet.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {anyPlaceholder && (
          <div className="flex items-start gap-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 p-3">
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-700 dark:text-amber-400">
              Diese Texte sind ausformulierte Vorlagen für das P1 — vollständig und
              unterschriftsreif, aber noch <b>nicht anwaltlich geprüft</b>. Lass sie einmal
              rechtlich abnehmen und speichere die freigegebene Fassung hier als neue Version;
              danach verschwindet dieser Hinweis.
            </p>
          </div>
        )}
        {loading ? (
          <p className="text-sm text-muted-foreground py-4">Lade…</p>
        ) : texts.map(t => (
          <div key={t.id} className="border border-border/60 rounded-lg p-3 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">{t.title}</span>
                <Badge variant="outline" className="text-[10px]">v{t.version}</Badge>
                {t.is_placeholder && <Badge className="text-[10px] bg-amber-500">Vorlage · ungeprüft</Badge>}
              </div>
            </div>
            <textarea
              value={drafts[t.type] ?? ''}
              onChange={(e) => setDrafts(d => ({ ...d, [t.type]: e.target.value }))}
              rows={5}
              className="w-full px-3 py-2 rounded-md border border-border bg-background text-sm resize-y font-mono"
              data-testid={`consent-text-${t.type}`}
            />
            {/* Vorlage: Freigabe auch ohne Textänderung möglich — aber erst nach einem zweiten,
                bewussten Klick, denn der Text gilt danach als geprüft. Freigegebene Texte: nur
                bei geändertem Wortlaut speichern. */}
            {t.is_placeholder && freigabeBestaetigen === t.type ? (
              <div className="flex items-center justify-end gap-2 flex-wrap" data-testid={`consent-freigabe-bestaetigen-${t.type}`}>
                <span className="text-xs text-amber-700 dark:text-amber-400">
                  Wirklich freigeben? Der Text gilt danach als geprüft.
                </span>
                {/* Reihenfolge bewusst: „Abbrechen" steht rechts außen, also dort, wo eben noch
                    „Geprüfte Fassung freigeben" stand — ein Doppelklick bricht ab, statt freizugeben. */}
                <Button size="sm" onClick={() => save(t)} disabled={savingType === t.type}
                  data-testid={`consent-save-${t.type}`}>
                  <Save className="w-4 h-4 mr-1.5" /> {savingType === t.type ? 'Speichert…' : 'Ja, freigeben'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => setFreigabeBestaetigen(null)} disabled={savingType === t.type}>
                  Abbrechen
                </Button>
              </div>
            ) : (
              <div className="flex justify-end">
                <Button size="sm"
                  onClick={() => (t.is_placeholder ? setFreigabeBestaetigen(t.type) : save(t))}
                  disabled={savingType === t.type || (!t.is_placeholder && (drafts[t.type] ?? '') === t.body)}
                  data-testid={t.is_placeholder ? `consent-freigeben-${t.type}` : `consent-save-${t.type}`}>
                  <Save className="w-4 h-4 mr-1.5" /> {savingType === t.type
                    ? 'Speichert…'
                    : (t.is_placeholder ? 'Geprüfte Fassung freigeben' : 'Als neue Version speichern')}
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

/* ─── Beratungs-Verfügbarkeit (öffentliche Buchung /buchung/beratung) ───
   Nutzt den globalen Speichern-Button der Einstellungen (Feld settings.beratung). */
const BERATUNG_DEFAULT = {
  enabled: false, staff_user_id: '', dauer_minuten: 15, puffer_minuten: 5,
  vorlauf_stunden: 2, max_pro_tag: 6, vorschau_tage: 14,
  ort: 'P1 Performance Center, Eutingen',
  verfuegbarkeit: { mo: [['09:00', '12:00']], di: [['09:00', '12:00']], mi: [['09:00', '12:00']], do: [['09:00', '12:00']], fr: [['09:00', '12:00']], sa: [], so: [] },
};
const BERATUNG_TAGE = [
  ['mo', 'Montag'], ['di', 'Dienstag'], ['mi', 'Mittwoch'], ['do', 'Donnerstag'],
  ['fr', 'Freitag'], ['sa', 'Samstag'], ['so', 'Sonntag'],
];

function BeratungTab({ settings, setSettings, isAdmin }) {
  const [staff, setStaff] = useState([]);
  const [qr, setQr] = useState(null);
  const b = { ...BERATUNG_DEFAULT, ...(settings.beratung || {}) };
  const patch = (upd) => setSettings(p => ({ ...p, beratung: { ...BERATUNG_DEFAULT, ...(p.beratung || {}), ...upd } }));

  useEffect(() => {
    authAPI.users().then(res => setStaff(res.data || [])).catch(() => {});
  }, []);

  const loadQr = async () => {
    try {
      const res = await beratungAdminAPI.qr();
      setQr(res.data);
    } catch { toast.error('QR-Code konnte nicht geladen werden'); }
  };

  const setDay = (key, windows) => patch({ verfuegbarkeit: { ...(b.verfuegbarkeit || {}), [key]: windows } });

  return (
    <div className="space-y-4">
      <Card className="border-border/60">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Öffentliche Beratungsbuchung</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between bg-muted/30 rounded-lg p-3">
            <div>
              <p className="text-sm font-medium">Online-Buchung aktiv</p>
              <p className="text-xs text-muted-foreground">Interessenten können unter /buchung/beratung selbst Termine buchen (QR-Code)</p>
            </div>
            <Switch checked={!!b.enabled} onCheckedChange={(v) => patch({ enabled: v })} disabled={!isAdmin} data-testid="beratung-enabled" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Zuständige Mitarbeiterin / Mitarbeiter</Label>
              <select value={b.staff_user_id || ''} onChange={(e) => patch({ staff_user_id: e.target.value })} disabled={!isAdmin}
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm" data-testid="beratung-staff">
                <option value="">— bitte wählen —</option>
                {staff.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
              </select>
              <p className="text-[10px] text-muted-foreground mt-1">Termine landen im Kalender dieser Person; deren PT-Termine blockieren Slots.</p>
            </div>
            <div>
              <Label className="text-xs">Ort (steht in Bestätigung & Kalendereintrag)</Label>
              <Input value={b.ort || ''} onChange={(e) => patch({ ort: e.target.value })} disabled={!isAdmin} data-testid="beratung-ort" />
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <div><Label className="text-xs">Dauer (Min.)</Label>
              <Input type="number" min="5" max="120" value={b.dauer_minuten} onChange={(e) => patch({ dauer_minuten: parseInt(e.target.value) || 15 })} disabled={!isAdmin} data-testid="beratung-dauer" /></div>
            <div><Label className="text-xs">Puffer (Min.)</Label>
              <Input type="number" min="0" max="60" value={b.puffer_minuten} onChange={(e) => patch({ puffer_minuten: parseInt(e.target.value) || 0 })} disabled={!isAdmin} data-testid="beratung-puffer" /></div>
            <div><Label className="text-xs">Vorlauf (Std.)</Label>
              <Input type="number" min="0" max="168" value={b.vorlauf_stunden} onChange={(e) => patch({ vorlauf_stunden: parseInt(e.target.value) || 0 })} disabled={!isAdmin} data-testid="beratung-vorlauf" /></div>
            <div><Label className="text-xs">Max. / Tag</Label>
              <Input type="number" min="1" max="50" value={b.max_pro_tag} onChange={(e) => patch({ max_pro_tag: parseInt(e.target.value) || 6 })} disabled={!isAdmin} data-testid="beratung-maxtag" /></div>
            <div><Label className="text-xs">Vorschau (Tage)</Label>
              <Input type="number" min="1" max="60" value={b.vorschau_tage} onChange={(e) => patch({ vorschau_tage: parseInt(e.target.value) || 14 })} disabled={!isAdmin} data-testid="beratung-vorschau" /></div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader className="pb-3"><CardTitle className="text-sm">Verfügbarkeit (Wochentage & Zeitfenster)</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {BERATUNG_TAGE.map(([key, label]) => {
            const win = (b.verfuegbarkeit?.[key] || [])[0];
            const aktiv = !!win;
            return (
              <div key={key} className="flex items-center gap-3 py-1.5 border-b border-border/30 last:border-0" data-testid={`beratung-tag-${key}`}>
                <label className="flex items-center gap-2 w-32 cursor-pointer">
                  <input type="checkbox" checked={aktiv} disabled={!isAdmin}
                    onChange={(e) => setDay(key, e.target.checked ? [['09:00', '12:00']] : [])}
                    className="w-4 h-4 accent-marke" />
                  <span className="text-sm">{label}</span>
                </label>
                {aktiv && (
                  <div className="flex items-center gap-2">
                    <Input type="time" value={win[0]} disabled={!isAdmin} className="w-28 h-8 text-xs"
                      onChange={(e) => setDay(key, [[e.target.value, win[1]]])} />
                    <span className="text-xs text-muted-foreground">bis</span>
                    <Input type="time" value={win[1]} disabled={!isAdmin} className="w-28 h-8 text-xs"
                      onChange={(e) => setDay(key, [[win[0], e.target.value]])} />
                  </div>
                )}
              </div>
            );
          })}
          <p className="text-[10px] text-muted-foreground pt-1">Slots werden im Raster Dauer + Puffer innerhalb dieser Fenster angeboten. Änderungen mit dem Speichern-Button oben übernehmen.</p>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardHeader className="pb-3"><CardTitle className="text-sm">QR-Code für die Buchungsseite</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {!qr ? (
            <Button variant="outline" size="sm" onClick={loadQr} data-testid="beratung-qr-btn">QR-Code anzeigen</Button>
          ) : (
            <div className="flex flex-col sm:flex-row items-center gap-4">
              <img src={qr.qr_code} alt="QR-Code Beratungsbuchung" className="w-40 h-40 border border-border rounded-lg" data-testid="beratung-qr-img" />
              <div className="text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">{qr.url}</p>
                <p>QR-Code ausdrucken/aushängen — Interessenten scannen und buchen direkt.</p>
                <p>Rechtsklick auf das Bild → „Bild speichern" für Druckmaterial.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function DatenschutzTab() {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(false);
  const [exporting, setExporting] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [consentUpdating, setConsentUpdating] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [processors, setProcessors] = useState([]);
  const [loadingProcessors, setLoadingProcessors] = useState(false);
  const [newProcessor, setNewProcessor] = useState({ name: '', purpose: '', location: '', avv_signed: false });
  const [addingProcessor, setAddingProcessor] = useState(false);

  // Search leads
  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await leadsAPI.getAll({ search: searchQuery, limit: 20 });
      setSearchResults(res.data.leads || res.data || []);
    } catch {
      toast.error('Suche fehlgeschlagen');
    } finally {
      setSearching(false);
    }
  };

  // Load audit log
  const loadAuditLog = async () => {
    setLoadingAudit(true);
    try {
      const res = await dsgvoAPI.auditLog({ limit: 50 });
      setAuditLog(res.data.entries || []);
    } catch {
      toast.error('Audit-Log konnte nicht geladen werden');
    } finally {
      setLoadingAudit(false);
    }
  };

  useEffect(() => { loadAuditLog(); }, []);

  const loadRequests = async () => {
    setLoadingRequests(true);
    try {
      const res = await dsgvoAPI.listRequests({ status: 'pending' });
      setRequests(res.data.requests || []);
    } catch { toast.error('Löschanfragen konnten nicht geladen werden'); }
    finally { setLoadingRequests(false); }
  };
  useEffect(() => { loadRequests(); }, []);

  const loadProcessors = async () => {
    setLoadingProcessors(true);
    try {
      const res = await processorsAPI.list();
      setProcessors(res.data.processors || []);
    } catch { toast.error('Dienstleister konnten nicht geladen werden'); }
    finally { setLoadingProcessors(false); }
  };
  useEffect(() => { loadProcessors(); }, []);

  const handleSetConsent = async (lead, granted) => {
    setConsentUpdating(lead.id);
    try {
      await dsgvoAPI.setConsent(lead.id, granted);
      toast.success(`Marketing-Einwilligung ${granted ? 'erteilt' : 'widerrufen'} für "${lead.name}"`);
      setSearchResults(prev => prev.map(l => l.id === lead.id ? { ...l, marketing_consent: granted } : l));
      loadAuditLog();
    } catch { toast.error('Aktualisierung fehlgeschlagen'); }
    finally { setConsentUpdating(null); }
  };

  const handleReviewRequest = async (id, action) => {
    try {
      const res = await dsgvoAPI.reviewRequest(id, action);
      toast.success(res.data.message);
      loadRequests();
      loadAuditLog();
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktion fehlgeschlagen'); }
  };

  const handleAddProcessor = async () => {
    if (!newProcessor.name.trim() || !newProcessor.purpose.trim()) {
      toast.error('Name und Zweck sind erforderlich');
      return;
    }
    setAddingProcessor(true);
    try {
      await processorsAPI.create({ ...newProcessor, data_categories: [] });
      toast.success('Dienstleister hinzugefügt');
      setNewProcessor({ name: '', purpose: '', location: '', avv_signed: false });
      loadProcessors();
    } catch { toast.error('Hinzufügen fehlgeschlagen'); }
    finally { setAddingProcessor(false); }
  };

  const handleToggleAvv = async (proc) => {
    try {
      await processorsAPI.update(proc.id, { avv_signed: !proc.avv_signed });
      loadProcessors();
    } catch { toast.error('Aktualisierung fehlgeschlagen'); }
  };

  const handleDeleteProcessor = async (id) => {
    try {
      await processorsAPI.remove(id);
      toast.success('Dienstleister entfernt');
      loadProcessors();
    } catch { toast.error('Entfernen fehlgeschlagen'); }
  };

  // Export lead data
  const handleExport = async (lead) => {
    setExporting(lead.id);
    try {
      const res = await dsgvoAPI.exportData(lead.id);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dsgvo_export_${lead.name?.replace(/\s+/g, '_') || lead.id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Daten von "${lead.name}" exportiert`);
      loadAuditLog();
    } catch {
      toast.error('Export fehlgeschlagen');
    } finally {
      setExporting(null);
    }
  };

  // Delete lead data completely
  const handleDelete = async (lead) => {
    setDeleting(lead.id);
    try {
      const res = await dsgvoAPI.deleteData(lead.id);
      const counts = res.data.geloeschte_datensaetze || {};
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(`${total} Datensätze von "${lead.name}" gelöscht (DSGVO Art. 17)`);
      setSearchResults(prev => prev.filter(l => l.id !== lead.id));
      setConfirmDelete(null);
      loadAuditLog();
    } catch {
      toast.error('Löschung fehlgeschlagen');
    } finally {
      setDeleting(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="datenschutz-tab">
      {/* Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Shield className="w-4 h-4" />DSGVO-Funktionen</CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-1">
          <p>Dieses CRM unterstützt die folgenden DSGVO-Rechte:</p>
          <ul className="list-disc ml-5 space-y-1 mt-2">
            <li><strong>Art. 15 — Auskunftsrecht:</strong> Alle personenbezogenen Daten eines Leads als JSON exportieren</li>
            <li><strong>Art. 17 — Recht auf Löschung:</strong> Alle Daten aus sämtlichen Collections unwiderruflich löschen</li>
            <li><strong>Art. 5 — Rechenschaftspflicht:</strong> Audit-Log aller Datenexporte und -löschungen</li>
          </ul>
        </CardContent>
      </Card>

      {/* Search & Actions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Search className="w-4 h-4" />Person suchen</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 mb-4">
            <Input
              placeholder="Name, E-Mail oder Telefon..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              data-testid="dsgvo-search-input"
            />
            <Button onClick={handleSearch} disabled={searching} data-testid="dsgvo-search-btn">
              {searching ? 'Suche...' : 'Suchen'}
            </Button>
          </div>

          {searchResults.length > 0 && (
            <div className="border rounded-lg divide-y">
              {searchResults.map(lead => (
                <div key={lead.id} className="flex items-center justify-between px-4 py-3" data-testid={`dsgvo-lead-${lead.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium text-sm truncate">{lead.name}</p>
                    <p className="text-xs text-muted-foreground">{lead.email} · {lead.phone}</p>
                    <Badge variant={lead.marketing_consent === false ? 'destructive' : lead.marketing_consent === true ? 'default' : 'secondary'} className="text-[9px] mt-1">
                      Marketing: {lead.marketing_consent === false ? 'widerrufen' : lead.marketing_consent === true ? 'erteilt' : 'nicht erfragt'}
                    </Badge>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleSetConsent(lead, true)}
                      disabled={consentUpdating === lead.id || lead.marketing_consent === true}
                      title="Einwilligung erteilen"
                      data-testid={`dsgvo-consent-grant-${lead.id}`}
                    >
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleSetConsent(lead, false)}
                      disabled={consentUpdating === lead.id || lead.marketing_consent === false}
                      title="Einwilligung widerrufen"
                      data-testid={`dsgvo-consent-revoke-${lead.id}`}
                    >
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={() => handleExport(lead)}
                      disabled={exporting === lead.id}
                      data-testid={`dsgvo-export-${lead.id}`}
                    >
                      <Download className="w-3.5 h-3.5 mr-1" />
                      {exporting === lead.id ? 'Export...' : 'Export'}
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      onClick={async () => {
                        try {
                          await dsgvoAPI.createRequest({ lead_id: lead.id, type: 'delete', notes: 'Über CRM angefragt' });
                          toast.success('Löschanfrage erstellt');
                          loadRequests();
                        } catch { toast.error('Anfrage fehlgeschlagen'); }
                      }}
                      title="Löschanfrage stellen (statt sofort löschen)"
                      data-testid={`dsgvo-request-delete-${lead.id}`}
                    >
                      <Inbox className="w-3.5 h-3.5 mr-1" />Anfrage
                    </Button>
                    {confirmDelete === lead.id ? (
                      <div className="flex gap-1">
                        <Button size="sm" variant="destructive" onClick={() => handleDelete(lead)} disabled={deleting === lead.id} data-testid={`dsgvo-confirm-delete-${lead.id}`}>
                          {deleting === lead.id ? 'Lösche...' : 'Endgültig löschen'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setConfirmDelete(null)}>Abbrechen</Button>
                      </div>
                    ) : (
                      <Button
                        size="sm" variant="destructive" className="bg-red-600 hover:bg-red-700"
                        onClick={() => setConfirmDelete(lead.id)}
                        data-testid={`dsgvo-delete-${lead.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5 mr-1" />Löschen
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          {searchResults.length === 0 && searchQuery && !searching && (
            <p className="text-sm text-muted-foreground text-center py-4">Keine Ergebnisse für "{searchQuery}"</p>
          )}
        </CardContent>
      </Card>

      {/* Teilnehmerliste in der App: Betreiber-Schalter (speichert selbst, nicht über den
          globalen Speichern-Knopf). Ersetzt die frühere Einwilligung teilnahme_sichtbar. */}
      <TeilnehmerlisteKarte />

      {/* Einwilligungstexte (App-Waiver) */}
      <ConsentTextsCard />

      {/* Audit Log */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Eye className="w-4 h-4" />Audit-Log (DSGVO Art. 5)</CardTitle>
          <Button size="sm" variant="outline" onClick={loadAuditLog} disabled={loadingAudit} data-testid="audit-log-refresh">
            Aktualisieren
          </Button>
        </CardHeader>
        <CardContent>
          {auditLog.length > 0 ? (
            <div className="border rounded-lg divide-y max-h-80 overflow-y-auto">
              {auditLog.map((entry, i) => (
                <div key={`${entry.timestamp}-${entry.target_id}-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm" data-testid={`audit-entry-${i}`}>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${entry.action === 'daten_loeschung' ? 'bg-red-500' : entry.action === 'daten_export' ? 'bg-blue-500' : 'bg-gray-400'}`} />
                  <Clock className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-xs text-muted-foreground w-36 flex-shrink-0">{new Date(entry.timestamp).toLocaleString('de-DE')}</span>
                  <Badge variant={entry.action === 'daten_loeschung' ? 'destructive' : 'secondary'} className="text-[10px]">
                    {entry.action === 'daten_export' ? 'Export' : entry.action === 'daten_loeschung' ? 'Löschung' : entry.action}
                  </Badge>
                  <span className="text-xs truncate">{entry.details}</span>
                  <span className="text-xs text-muted-foreground ml-auto flex-shrink-0">{entry.user_name}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              {loadingAudit ? 'Lade Audit-Log...' : 'Noch keine DSGVO-Aktionen protokolliert'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Löschanfragen */}
      <Card>
        <CardHeader className="pb-3 flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2"><Inbox className="w-4 h-4" />Offene Löschanfragen</CardTitle>
          <Button size="sm" variant="outline" onClick={loadRequests} disabled={loadingRequests}>Aktualisieren</Button>
        </CardHeader>
        <CardContent>
          {requests.length > 0 ? (
            <div className="border rounded-lg divide-y">
              {requests.map(r => (
                <div key={r.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`privacy-request-${r.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{r.lead_name} <Badge variant="secondary" className="text-[9px] ml-1">{r.type}</Badge></p>
                    <p className="text-xs text-muted-foreground">{r.notes || 'Keine Notiz'} · angefragt von {r.requested_by_name}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" variant="outline" onClick={() => handleReviewRequest(r.id, 'approve')} data-testid={`privacy-request-approve-${r.id}`}>Genehmigen</Button>
                    <Button size="sm" variant="destructive" onClick={() => handleReviewRequest(r.id, 'reject')}>Ablehnen</Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              {loadingRequests ? 'Lade...' : 'Keine offenen Löschanfragen'}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Dienstleister & AVV */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Dienstleister &amp; AVV-Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
            <Input placeholder="Name (z.B. Twilio)" value={newProcessor.name} onChange={e => setNewProcessor(p => ({ ...p, name: e.target.value }))} className="h-9 text-xs" data-testid="processor-name-input" />
            <Input placeholder="Zweck (z.B. SMS-Versand)" value={newProcessor.purpose} onChange={e => setNewProcessor(p => ({ ...p, purpose: e.target.value }))} className="h-9 text-xs" />
            <Input placeholder="Standort (z.B. USA)" value={newProcessor.location} onChange={e => setNewProcessor(p => ({ ...p, location: e.target.value }))} className="h-9 text-xs" />
            <Button size="sm" onClick={handleAddProcessor} disabled={addingProcessor} className="gap-1.5" data-testid="processor-add-btn">
              <Plus className="w-3.5 h-3.5" />Hinzufügen
            </Button>
          </div>
          {processors.length > 0 ? (
            <div className="border rounded-lg divide-y">
              {processors.map(p => (
                <div key={p.id} className="flex items-center justify-between px-4 py-3 text-sm" data-testid={`processor-${p.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{p.name}</p>
                    <p className="text-xs text-muted-foreground">{p.purpose}{p.location ? ` · ${p.location}` : ''}</p>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <button onClick={() => handleToggleAvv(p)} className="flex items-center gap-1.5" data-testid={`processor-avv-toggle-${p.id}`}>
                      <Badge variant={p.avv_signed ? 'default' : 'secondary'} className="text-[9px]">
                        {p.avv_signed ? 'AVV vorhanden' : 'AVV fehlt'}
                      </Badge>
                    </button>
                    <Button size="sm" variant="ghost" onClick={() => handleDeleteProcessor(p.id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-4">
              {loadingProcessors ? 'Lade...' : 'Noch keine Dienstleister erfasst'}
            </p>
          )}
        </CardContent>
      </Card>

      <WhatsAppAufraeumenKarte />

      <DatenzugangKarte />
    </div>
  );
}
